import PropTypes from 'prop-types'
import { memo, useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { usePersonaContext } from 'app/services/PersonaProvider/PersonaProvider'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import prettyPrice from 'app/util/prettyPrice'
import BG from 'components/core/BG/BG'
import Button from 'components/core/Button/Button'
import Dismissible from 'components/core/Dismissible/Dismissible'
import Divider from 'components/core/Divider/Divider'
import ScrollRegion from 'components/core/ScrollRegion/ScrollRegion'
import Spinner from 'components/elements/Spinner/Spinner'
import TransitionSwap from 'components/elements/TransitionSwap/TransitionSwap'
import animationStyles from 'components/util/animation/animation.module.scss'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import classNames from 'lib/util/classNames'
import styles from './Cart.module.scss'
import Hero from './Hero/Hero'
import Item from './Item/Item'

/**
 * The `Cart`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Cart (props) {
  const { isCartOpen: isActive, setIsCartOpen } = useRootContext()
  const { maticToUsdMultiplier, storeData } = usePersonaContext()
  const {
    cart,
    cartCount,
    cartTotalPrice,
    checkoutCart,
    dismissCartLostSupply,
    isCartReady,
    lostSupplyCart,
    preferredCurrency,
    removeFromCart
  } = useUserContext()
  const isRender = useDelayedActiveState(isActive, 0, 0.3)
  const isVisible = useDelayedActiveState(isActive, 0.1, 0)
  const hasCart = useMemo(() => !!cartCount, [cartCount])
  const classNameOutput = useMemo(
    () =>
      classNames([
        styles.container,
        isRender && styles.render,
        isVisible && styles.visible,
        hasCart && styles.hasCart
      ]),
    [isVisible, isRender, hasCart]
  )
  const lastLabel = useRef('')
  const hasLostSupplyWarning = useMemo(
    () => !!lostSupplyCart.length,
    [lostSupplyCart]
  )

  /**
   * Memo: The total price
   */
  const prettyTotalPrice = useMemo(
    () => prettyPrice(cartTotalPrice, preferredCurrency, maticToUsdMultiplier),
    [cartTotalPrice, maticToUsdMultiplier, preferredCurrency]
  )

  /**
   * Memo: The buy label
   */
  const buyLabel = useMemo(() => {
    if (!cartCount) {
      return lastLabel.current
    }

    if (cartTotalPrice) {
      const res = <>Buy Now for {prettyTotalPrice}</>

      lastLabel.current = res

      return res
    }

    const res = <>Checkout Now</>

    lastLabel.current = res

    return res
  }, [cartCount, prettyTotalPrice, cartTotalPrice])

  /**
   * Memo: The items
   */
  const items = useMemo(() => {
    if (!storeData) {
      return []
    }

    return cart.map(productId =>
      storeData?.products.find(product => product.id === productId)
    )
  }, [cart, storeData])

  /**
   * Handle closing the burger menu
   */
  const handleClose = useCallback(() => {
    setIsCartOpen(false)
  }, [])

  /**
   * Handle dismiss lost items. Used when the cart initialized with items
   * that are now out of stock, to display a dissmissable message warning
   * the user that those items have been removed from their cart
   */
  const handleDismissLostItems = useCallback(() => {
    dismissCartLostSupply()
  }, [])

  /**
   * Handle click cart checkout
   */
  const handleClickCheckout = useCallback(() => {
    // setIsCartOpen(false)
    checkoutCart()
  }, [checkoutCart])

  const handleClickViewMarketplace = useCallback(() => {
    setIsCartOpen(false)
  }, [])

  return (
    <div className={classNameOutput}>
      {isRender && (
        <>
          <button className={styles.background} onClick={handleClose} />
          <div className={styles.display}>
            <div className={styles.wrapper}>
              <Hero isActive={isVisible} />
              <div className={styles.body}>
                <BG color="e" opacity="a" mask="to-bottom" />
                <Divider mask="none" />
                <div className={styles.content}>
                  <ScrollRegion
                    className={styles.scroll}
                    useScrollPrompt={false}
                  >
                    {hasLostSupplyWarning && (
                      <Dismissible onDismiss={handleDismissLostItems}>
                        <Dismissible.Title>Important Message</Dismissible.Title>
                        <Dismissible.Description>
                          Some items previously in your cart are now unavailable
                          and have been removed.
                        </Dismissible.Description>
                      </Dismissible>
                    )}
                    {!isCartReady && (
                      <div
                        className={classNames([
                          styles.placeholder,
                          animationStyles.fadeUp
                        ])}
                      >
                        <Spinner isActive={true} delayShow={0} />
                        <h1>Loading...</h1>
                      </div>
                    )}
                    {!cartCount && isCartReady && (
                      <div
                        className={classNames([
                          styles.placeholder,
                          animationStyles.fadeUp
                        ])}
                      >
                        <h1>You have no items in your cart.</h1>
                        <h2>Why not add something?</h2>
                        <Button
                          Tag={Link}
                          to="/marketplace"
                          color="j"
                          size="lg"
                          borderRadius="c"
                          icon="arrow_right"
                          side="right"
                          onClick={handleClickViewMarketplace}
                        >
                          Visit Marketplace
                        </Button>
                      </div>
                    )}
                    <div className={styles.list}>
                      {items.map(item => (
                        <Item
                          key={item.id}
                          {...item}
                          onRemove={removeFromCart}
                        />
                      ))}
                    </div>
                  </ScrollRegion>
                  <div className={styles.footer}>
                    <Button
                      className={styles.button}
                      size="xl"
                      color="c"
                      wide={true}
                      onClick={handleClickCheckout}
                    >
                      <TransitionSwap>{buyLabel}</TransitionSwap>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** @type {object} */
Cart.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
Cart.defaultProps = {
  className: ''
}

// Memoize
export default memo(Cart)
